
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Ticket, Clock, CheckCircle, XCircle, TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface DashboardMetricsProps {
  stats: {
    total: number;
    abertas: number;
    emAndamento: number;
    resolvidas: number;
    fechadas: number;
  };
}

const DashboardMetrics = ({ stats }: DashboardMetricsProps) => {
  const [animatedStats, setAnimatedStats] = useState({
    total: 0,
    abertas: 0,
    emAndamento: 0,
    resolvidas: 0,
    fechadas: 0,
  });

  useEffect(() => {
    const duration = 1000;
    const steps = 30;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setAnimatedStats({
        total: Math.floor(stats.total * progress),
        abertas: Math.floor(stats.abertas * progress),
        emAndamento: Math.floor(stats.emAndamento * progress),
        resolvidas: Math.floor(stats.resolvidas * progress),
        fechadas: Math.floor(stats.fechadas * progress),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedStats(stats);
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [stats]);

  const metrics = [
    {
      title: 'Total de Tickets',
      value: animatedStats.total,
      icon: Ticket,
      color: 'text-blue-600', 
      bgColor: 'bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100',
      borderColor: 'border-l-4 border-l-blue-500',
      iconBg: 'bg-blue-100',
      trend: 0,
    },
    {
      title: 'Em Aberto',
      value: animatedStats.abertas + animatedStats.emAndamento,
      icon: Clock,
      color: 'text-amber-600', 
      bgColor: 'bg-gradient-to-br from-amber-50 via-amber-50 to-amber-100',
      borderColor: 'border-l-4 border-l-amber-500',
      iconBg: 'bg-amber-100',
      trend: 0,
    },
    {
      title: 'Resolvidas',
      value: animatedStats.resolvidas,
      icon: CheckCircle,
      color: 'text-emerald-600', 
      bgColor: 'bg-gradient-to-br from-emerald-50 via-emerald-50 to-emerald-100',
      borderColor: 'border-l-4 border-l-emerald-500',
      iconBg: 'bg-emerald-100',
      trend: 1,
    },
    {
      title: 'Fechadas',
      value: animatedStats.fechadas,
      icon: XCircle,
      color: 'text-slate-600', 
      bgColor: 'bg-gradient-to-br from-slate-50 via-slate-50 to-slate-100',
      borderColor: 'border-l-4 border-l-slate-400',
      iconBg: 'bg-slate-100',
      trend: 0,
    },
  ];

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return <TrendingUp className="h-3 w-3 text-emerald-600" />;
    if (trend < 0) return <TrendingDown className="h-3 w-3 text-red-600" />;
    return <Minus className="h-3 w-3 text-slate-400" />;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6">
      {metrics.map((metric, index) => {
        const IconComponent = metric.icon;
        return (
          <Card 
            key={metric.title} 
            className={`${metric.borderColor} card-lift group relative overflow-hidden animate-fade-in`}
            style={{ '--tw-animation-delay': `${index * 0.1}s` } as React.CSSProperties}
          >
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/50 to-transparent rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500"></div>
            
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 relative">
              <CardTitle className="text-sm font-semibold text-slate-700">
                {metric.title}
              </CardTitle>
              <div className={`p-2.5 rounded-xl ${metric.iconBg} shadow-sm group-hover:shadow-md transition-all duration-300 group-hover:scale-110`}>
                <IconComponent className={`h-5 w-5 ${metric.color}`} />
              </div>
            </CardHeader>
            
            <CardContent className="relative">
              <div className="flex items-end justify-between">
                <div className={`text-3xl font-display font-bold ${metric.color} animate-count-up`}>
                  {metric.value}
                </div>
                {metric.trend !== 0 && (
                  <div className="flex items-center gap-1 mb-1">
                    {getTrendIcon(metric.trend)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default DashboardMetrics;
